/**
 * Agent authority (ADR-0092, Accepted 2026-10-21) — declared authority
 * lifecycle, the deterministic drift comparator, and the AS-2/AS-4 check
 * derivations.
 *
 * Owner decisions baked in:
 *  - SoD: any member drafts; a version activates on owner/admin approval with
 *    approver ≠ author (the route enforces the role; this service enforces
 *    author ≠ approver and the state machine).
 *  - AS-2 substance: a declaration with no prohibited action and no numeric
 *    limit yields insufficient_evidence — breadth cannot game the check.
 *  - Drift severity by drifted scope class (versioned mapping below; a
 *    review-queue content override supersedes it when authored).
 *  - Drift fires on `exact`-confidence observed facts only; `approximate`
 *    facts render on the panel, unfindinged.
 *
 * Pure core: no Cloudflare imports, no network, no LLM.
 */
import { createFinding, SLA_HOURS } from './finding-service.js';
import { recordCheck } from './control-check-service.js';
export const SCOPE_CLASSES = ['read', 'write', 'execute', 'financial', 'communication', 'admin'];
const AUTONOMY_LEVELS = [
    'fully_automated', 'human_in_the_loop', 'human_on_the_loop', 'decision_support', 'informational',
];
/**
 * ADR-0092 owner decision: severity by DRIFTED scope class. Versioned here as
 * normalization content v111.1; a queue-authored override (Track 2 content)
 * supersedes this table without a code change.
 */
export const DRIFT_SEVERITY_BY_CLASS = {
    admin: 'high',
    financial: 'high',
    execute: 'high',
    write: 'medium',
    communication: 'medium',
    read: 'low',
};
export const AUTHORITY_NORMALIZATION_VERSION = 'v111.1';
export class AuthorityValidationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'AuthorityValidationError';
    }
}
export class AuthorityStateError extends Error {
    constructor(message) {
        super(message);
        this.name = 'AuthorityStateError';
    }
}
function parseTools(json) {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v : [];
}
function parseCeiling(json) {
    const v = JSON.parse(json);
    return v && typeof v === 'object' ? v : {};
}
export function parseContainment(json) {
    const v = JSON.parse(json);
    return v && typeof v === 'object' ? v : {};
}
function validateDeclaration(input) {
    if (!AUTONOMY_LEVELS.includes(input.autonomy_level)) {
        throw new AuthorityValidationError(`Unknown autonomy_level "${input.autonomy_level}".`);
    }
    for (const t of input.tools) {
        if (!t.name || typeof t.name !== 'string' || t.name.length > 200) {
            throw new AuthorityValidationError('Every declared tool needs a name (<= 200 chars).');
        }
        if (!SCOPE_CLASSES.includes(t.scope_class)) {
            throw new AuthorityValidationError(`Tool "${t.name}": unknown scope_class "${t.scope_class}".`);
        }
    }
    for (const c of input.action_ceiling.authorized_action_classes ?? []) {
        if (!SCOPE_CLASSES.includes(c)) {
            throw new AuthorityValidationError(`Unknown authorized action class "${c}".`);
        }
    }
    const limits = input.action_ceiling.limits ?? {};
    for (const [k, v] of Object.entries(limits)) {
        if (v !== undefined && (typeof v !== 'number' || v < 0)) {
            throw new AuthorityValidationError(`Ceiling limit "${k}" must be a non-negative number.`);
        }
    }
}
/**
 * AS-2 substance rule (owner, 2026-10-21): a declaration must carry at least
 * one prohibited action or one numeric limit. An everything-allowed
 * declaration cannot pass the boundary check.
 */
export function declarationHasSubstance(ceiling) {
    if ((ceiling.prohibited_actions ?? []).some((p) => typeof p === 'string' && p.trim().length > 0))
        return true;
    const limits = ceiling.limits ?? {};
    return Object.values(limits).some((v) => typeof v === 'number');
}
export async function getAuthority(db, workspaceId, systemId) {
    const rows = await db.query(`SELECT * FROM ai_system_authority
      WHERE workspace_id = ? AND system_id = ? AND status IN ('active', 'draft')
      ORDER BY version DESC`, [workspaceId, systemId]);
    return {
        active: rows.results.find((r) => r.status === 'active') ?? null,
        draft: rows.results.find((r) => r.status === 'draft') ?? null,
    };
}
export async function draftAuthority(db, input) {
    validateDeclaration(input);
    const { draft } = await getAuthority(db, input.workspace_id, input.system_id);
    if (draft) {
        throw new AuthorityStateError('A draft already exists for this system; edit or reject it first.');
    }
    const max = await db.query(`SELECT MAX(version) AS v FROM ai_system_authority WHERE system_id = ? AND workspace_id = ?`, [input.system_id, input.workspace_id]);
    const version = (max.results[0]?.v ?? 0) + 1;
    const id = `auth-${crypto.randomUUID()}`;
    await db.execute(`INSERT INTO ai_system_authority (
       authority_id, workspace_id, system_id, version, autonomy_level,
       tools_json, action_ceiling_json, containment_json,
       monitoring_owner_user_id, status, created_by_user_id, created_at
     ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, 'draft', ?, ?)`, [
        id, input.workspace_id, input.system_id, version, input.autonomy_level,
        JSON.stringify(input.tools), JSON.stringify(input.action_ceiling), JSON.stringify(input.containment),
        input.monitoring_owner_user_id ?? null, input.created_by_user_id, new Date().toISOString(),
    ]);
    const row = await db.query(`SELECT * FROM ai_system_authority WHERE authority_id = ? AND workspace_id = ?`, [id, input.workspace_id]);
    return row.results[0];
}
export async function updateDraft(db, input) {
    validateDeclaration(input);
    const before = (await db.query(`SELECT * FROM ai_system_authority WHERE authority_id = ? AND workspace_id = ? AND status = 'draft'`, [input.authority_id, input.workspace_id])).results[0];
    if (!before)
        throw new AuthorityStateError('No editable draft with that id in this workspace.');
    const editors = new Set(parseEditors(before.edited_by_json));
    if (input.editor_user_id && input.editor_user_id !== before.created_by_user_id)
        editors.add(input.editor_user_id);
    await db.execute(`UPDATE ai_system_authority
        SET autonomy_level = ?, tools_json = ?, action_ceiling_json = ?,
            containment_json = ?, monitoring_owner_user_id = ?, edited_by_json = ?
      WHERE authority_id = ? AND workspace_id = ? AND status = 'draft'`, [
        input.autonomy_level, JSON.stringify(input.tools), JSON.stringify(input.action_ceiling),
        JSON.stringify(input.containment), input.monitoring_owner_user_id ?? null, JSON.stringify([...editors]),
        input.authority_id, input.workspace_id,
    ]);
    const row = await db.query(`SELECT * FROM ai_system_authority WHERE authority_id = ? AND workspace_id = ?`, [input.authority_id, input.workspace_id]);
    const updated = row.results[0];
    if (!updated || updated.status !== 'draft') {
        throw new AuthorityStateError('No editable draft with that id in this workspace.');
    }
    return updated;
}
function parseEditors(json) {
    try {
        const v = JSON.parse(json ?? '[]');
        return Array.isArray(v) ? v.map(String) : [];
    }
    catch {
        return [];
    }
}
/** Everyone who shaped a draft's content: its author and every editor. None may approve it. */
export function draftContributors(draft) {
    return [draft.created_by_user_id, ...parseEditors(draft.edited_by_json)];
}
/**
 * ADR-0092 §6: the system's declared authority is AUTHORITATIVE for autonomy,
 * and use-case intake writes through to it. Declarations are SoD-gated, so
 * intake never changes the ACTIVE version: it edits the open draft's
 * autonomy, or opens a draft copying the active declaration with the new
 * autonomy, for an owner/admin to approve. No change → no draft.
 *
 * Returns the draft written (or null when nothing changed, or the level is
 * outside the shared vocabulary — intake's free value is never coerced).
 */
export async function writeThroughUseCaseAutonomy(db, input) {
    if (!AUTONOMY_LEVELS.includes(input.autonomy_level))
        return null;
    const level = input.autonomy_level;
    const { active, draft } = await getAuthority(db, input.workspace_id, input.system_id);
    if (draft) {
        if (draft.autonomy_level === level)
            return null;
        const updated = await updateDraft(db, {
            workspace_id: input.workspace_id,
            authority_id: draft.authority_id,
            autonomy_level: level,
            tools: parseTools(draft.tools_json),
            action_ceiling: parseCeiling(draft.action_ceiling_json),
            containment: parseContainment(draft.containment_json),
            monitoring_owner_user_id: draft.monitoring_owner_user_id,
            editor_user_id: input.created_by_user_id,
        });
        return { draft: updated, created: false };
    }
    if (active && active.autonomy_level === level)
        return null;
    const created = await draftAuthority(db, {
        workspace_id: input.workspace_id,
        system_id: input.system_id,
        autonomy_level: level,
        tools: active ? parseTools(active.tools_json) : [],
        action_ceiling: active ? parseCeiling(active.action_ceiling_json) : {},
        containment: active ? parseContainment(active.containment_json) : {},
        monitoring_owner_user_id: active?.monitoring_owner_user_id ?? null,
        created_by_user_id: input.created_by_user_id,
    });
    return { draft: created, created: true };
}
/**
 * Activate a draft. SoD: approver ≠ author (also a table CHECK). The route
 * layer enforces the owner/admin role; this function enforces the rest.
 */
export async function approveAuthority(db, input) {
    const row = await db.query(`SELECT * FROM ai_system_authority WHERE authority_id = ? AND workspace_id = ?`, [input.authority_id, input.workspace_id]);
    const draft = row.results[0];
    if (!draft || draft.status !== 'draft')
        throw new AuthorityStateError('No pending draft with that id.');
    if (draft.created_by_user_id === input.approver_user_id) {
        throw new AuthorityStateError('Separation of duties: the author cannot approve their own declaration.');
    }
    // SoD covers everyone who shaped the content: an editor who rewrote a
    // draft cannot then approve it (2026-10-21 fix; migration 708).
    if (parseEditors(draft.edited_by_json).includes(input.approver_user_id)) {
        throw new AuthorityStateError('Separation of duties: you edited this draft, so someone else approves it.');
    }
    const now = new Date().toISOString();
    await db.execute(`UPDATE ai_system_authority SET status = 'superseded', superseded_at = ?
      WHERE system_id = ? AND workspace_id = ? AND status = 'active'`, [now, draft.system_id, input.workspace_id]);
    await db.execute(`UPDATE ai_system_authority
        SET status = 'active', approved_by_user_id = ?, approved_at = ?,
            supersedes_authority_id = (
              SELECT authority_id FROM ai_system_authority
               WHERE system_id = ? AND workspace_id = ? AND status = 'superseded'
               ORDER BY superseded_at DESC LIMIT 1
            )
      WHERE authority_id = ? AND workspace_id = ?`, [input.approver_user_id, now, draft.system_id, input.workspace_id, input.authority_id, input.workspace_id]);
    const activated = await db.query(`SELECT * FROM ai_system_authority WHERE authority_id = ? AND workspace_id = ?`, [input.authority_id, input.workspace_id]);
    return activated.results[0];
}
export async function rejectDraft(db, input) {
    await db.execute(`UPDATE ai_system_authority SET status = 'rejected'
      WHERE authority_id = ? AND workspace_id = ? AND status = 'draft'`, [input.authority_id, input.workspace_id]);
}
export function declaredScopeClasses(declared) {
    const tools = parseTools(declared.tools_json);
    const ceiling = parseCeiling(declared.action_ceiling_json);
    return new Set([
        ...tools.map((t) => t.scope_class),
        ...(ceiling.authorized_action_classes ?? []),
    ]);
}
/**
 * Compare a system's observed authority against its ACTIVE declaration and
 * reconcile `agent_authority_drift` findings: one per system × drifted scope
 * class, deduped, auto-closed when the drift clears. One-directional —
 * declared-but-not-observed is never a finding (ADR-0092 §3).
 */
export async function reconcileAgentAuthority(db, workspaceId, systemId, now = new Date().toISOString()) {
    const { active } = await getAuthority(db, workspaceId, systemId);
    const facts = await db.query(`SELECT fact_id, fact_kind, provider_key, name, scope_class, summary_confidence, gone_at
       FROM observed_agent_authority
      WHERE workspace_id = ? AND system_id = ? AND gone_at IS NULL
        AND fact_kind <> 'authority_read_unavailable'`, [workspaceId, systemId]);
    const result = { drifted: new Map(), approximate_outside: [], opened: 0, closed: 0 };
    // No active declaration: nothing to compare against. AS-2 carries that gap
    // (insufficient_evidence); drift findings would only duplicate it.
    const declared = active ? declaredScopeClasses(active) : null;
    if (declared) {
        for (const f of facts.results) {
            if (f.scope_class === 'unknown')
                continue;
            if (declared.has(f.scope_class))
                continue;
            if (f.summary_confidence !== 'exact') {
                result.approximate_outside.push(f.name);
                continue;
            }
            const names = result.drifted.get(f.scope_class) ?? [];
            names.push(f.name);
            result.drifted.set(f.scope_class, names);
        }
    }
    // idx_findings_dedup is unique on (workspace_id, dedup_key) across every status, so a
    // re-drift after an auto-close reopens the closed row instead of inserting a second one.
    const existing = await db.query(`SELECT finding_id, dedup_key, status FROM findings
      WHERE workspace_id = ? AND kind = 'agent_authority_drift'
        AND dedup_key LIKE ?`, [workspaceId, `agent_authority_drift:${systemId}:%`]);
    const openByKey = new Map(existing.results.filter((r) => r.status === 'open' || r.status === 'acknowledged').map((r) => [r.dedup_key, r.finding_id]));
    const closedByKey = new Map(existing.results.filter((r) => r.status !== 'open' && r.status !== 'acknowledged').map((r) => [r.dedup_key, r.finding_id]));
    for (const [scopeClass, names] of result.drifted) {
        const key = `agent_authority_drift:${systemId}:${scopeClass}`;
        if (openByKey.has(key))
            continue;
        const sample = names.slice(0, 5).join(', ');
        const detail = `Observed agent authority exceeds the declaration: ${names.length} ${scopeClass}-class capabilit${names.length === 1 ? 'y' : 'ies'} (${sample}${names.length > 5 ? ', …' : ''}) with no ${scopeClass} grant in the active declared authority (normalization ${AUTHORITY_NORMALIZATION_VERSION}, exact-confidence facts only). Declare it, or remove the capability.`;
        const severity = DRIFT_SEVERITY_BY_CLASS[scopeClass];
        const closedId = closedByKey.get(key);
        if (closedId) {
            const slaHours = SLA_HOURS[severity];
            await db.execute(`UPDATE findings SET status = 'open', closed_at = NULL, severity = ?, detail = ?,
                sla_hours = ?, sla_deadline_at = ?, sla_breached = 0, sla_breached_at = NULL,
                status_reason = 'reopened: authority drift observed again', updated_at = ?
          WHERE finding_id = ? AND workspace_id = ?`, [severity, detail, slaHours, new Date(Date.parse(now) + slaHours * 3_600_000).toISOString(), now, closedId, workspaceId]);
            result.opened++;
            continue;
        }
        await createFinding(db, {
            finding_id: `fnd-${crypto.randomUUID()}`,
            workspace_id: workspaceId,
            check_id: null,
            control_id: 'AS-2',
            system_id: systemId,
            connector_id: null,
            severity,
            kind: 'agent_authority_drift',
            detail,
            dedup_key: key,
        });
        result.opened++;
    }
    for (const [key, findingId] of openByKey) {
        const scopeClass = key.slice(`agent_authority_drift:${systemId}:`.length);
        if (result.drifted.has(scopeClass))
            continue;
        await db.execute(`UPDATE findings SET status = 'auto_closed', closed_at = ?, updated_at = ?,
              status_reason = 'authority drift cleared'
        WHERE finding_id = ? AND workspace_id = ?`, [now, now, findingId, workspaceId]);
        result.closed++;
    }
    return result;
}
export async function evaluateAuthorityChecks(db, workspaceId, systemId) {
    const { active } = await getAuthority(db, workspaceId, systemId);
    const results = [];
    if (!active) {
        const details = { reason: 'no_active_declaration' };
        results.push({ check_key: 'as2_action_boundary', control_id: 'AS-2', status: 'insufficient_evidence', details });
        results.push({ check_key: 'as4_kill_switch', control_id: 'AS-4', status: 'insufficient_evidence', details });
        return results;
    }
    const ceiling = parseCeiling(active.action_ceiling_json);
    if (!declarationHasSubstance(ceiling)) {
        results.push({
            check_key: 'as2_action_boundary', control_id: 'AS-2', status: 'insufficient_evidence',
            details: { reason: 'declaration_without_substance', hint: 'Declare at least one prohibited action or numeric ceiling; an everything-allowed declaration cannot pass (ADR-0092).' },
        });
    }
    else {
        const drift = await db.query(`SELECT COUNT(*) AS n FROM findings
        WHERE workspace_id = ? AND system_id = ? AND kind = 'agent_authority_drift'
          AND status IN ('open', 'acknowledged')`, [workspaceId, systemId]);
        const openDrift = drift.results[0]?.n ?? 0;
        results.push({
            check_key: 'as2_action_boundary', control_id: 'AS-2',
            status: openDrift > 0 ? 'fail' : 'pass',
            details: { authority_version: active.version, open_drift_findings: openDrift },
        });
    }
    const containment = parseContainment(active.containment_json);
    if (!containment.kill_switch_kind || !containment.kill_switch_owner_user_id) {
        results.push({
            check_key: 'as4_kill_switch', control_id: 'AS-4', status: 'insufficient_evidence',
            details: { reason: 'containment_undeclared', missing: ['kill_switch_kind', 'kill_switch_owner_user_id'].filter((k) => !containment[k]) },
        });
    }
    else if (!containment.rollback_evidence_ref) {
        results.push({
            check_key: 'as4_kill_switch', control_id: 'AS-4', status: 'insufficient_evidence',
            details: { reason: 'rollback_evidence_missing' },
        });
    }
    else {
        const evidence = await db.query(`SELECT COUNT(*) AS n FROM evidence_artifacts WHERE artifact_id = ?`, [containment.rollback_evidence_ref]);
        const resolves = (evidence.results[0]?.n ?? 0) > 0;
        results.push({
            check_key: 'as4_kill_switch', control_id: 'AS-4',
            status: resolves ? 'pass' : 'insufficient_evidence',
            details: resolves
                ? { authority_version: active.version, kill_switch_kind: containment.kill_switch_kind }
                : { reason: 'rollback_evidence_unresolved', ref: containment.rollback_evidence_ref },
        });
    }
    return results;
}
export const AUTHORITY_CHECK_COLLECTOR_VERSION = 'agent-authority/v111.1';
/**
 * ADR-0092 §4: AS-2 / AS-4 are EMITTED as control_checks under the existing
 * recorder, so readiness, program views and the insurability pack read them
 * like any other check (until now they were computed only on panel reads).
 * Called by the collector run that just reconciled the system — that run
 * supplies the connector and the evidence store. The evidence object is the
 * derivation itself (status + details), content-addressed by the recorder.
 */
export async function recordAuthorityChecks(db, storage, input) {
    const results = await evaluateAuthorityChecks(db, input.workspace_id, input.system_id);
    for (const r of results) {
        const evidence = JSON.stringify({
            check_key: r.check_key, control_id: r.control_id, system_id: input.system_id,
            status: r.status, details: r.details, normalization: AUTHORITY_NORMALIZATION_VERSION,
        });
        await recordCheck(db, storage, {
            check_id: input.newId(),
            workspace_id: input.workspace_id,
            connector_id: input.connector_id,
            control_id: r.control_id,
            system_id: input.system_id,
            passed: r.status === 'pass',
            insufficient: r.status === 'insufficient_evidence',
            evidence_bytes: new TextEncoder().encode(evidence).buffer,
            collector_version: AUTHORITY_CHECK_COLLECTOR_VERSION,
            check_name: r.check_key,
            check_key: r.check_key,
            details: { check_key: r.check_key, ...r.details },
            // AS-2 failing means observed authority exceeds the declaration.
            finding_severity: 'high',
        });
    }
    return results;
}
/**
 * Verifiability markers (owner decision 2026-10-21): per declared item,
 * whether any collector fact corroborates it. Declared-only items render as
 * self-attested on the pack — never as verified fact.
 */
export function verificationSummary(declared, observed) {
    const live = observed.filter((f) => f.gone_at === null && f.fact_kind !== 'authority_read_unavailable');
    const observedClasses = new Set(live.map((f) => f.scope_class));
    const observedNames = new Set(live.map((f) => f.name.toLowerCase()));
    const out = [];
    for (const t of parseTools(declared.tools_json)) {
        out.push({
            kind: 'tool', name: t.name, scope_class: t.scope_class,
            verified: observedNames.has(t.name.toLowerCase()) || observedClasses.has(t.scope_class),
        });
    }
    for (const c of parseCeiling(declared.action_ceiling_json).authorized_action_classes ?? []) {
        out.push({ kind: 'action_class', name: c, scope_class: c, verified: observedClasses.has(c) });
    }
    return out;
}
//# sourceMappingURL=agent-authority-service.js.map